import { Box, Container, Grid, Paper, Typography, Button, Alert } from '@mui/material';
import {
  People as PeopleIcon,
  DirectionsRun as ActivityIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  Queue as QueueIcon,
  Refresh as RefreshIcon,
  DeleteSweep as ClearCacheIcon,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  type ChartOptions,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface SystemMetrics {
  totalUsers: {
    athlete: number;
    trainer: number;
    both: number;
    superuser: number;
  };
  activeUsers: number;
  totalActivities: number;
  dbSize: string;
  cacheHitRate: number;
  celeryQueues: {
    default: number;
    priority: number;
    sync: number;
  };
}

interface ServiceHealth {
  name: string;
  status: 'healthy' | 'degraded' | 'down';
  responseTime: number;
  lastCheck: string;
}

interface UserRegistration {
  date: string;
  count: number;
}

interface ErrorLog {
  timestamp: string;
  service: string;
  message: string;
  severity: 'error' | 'warning';
}

const MetricsCard = ({
  title,
  value,
  icon,
  color = '#0d47a1',
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
}) => (
  <Paper
    elevation={2}
    sx={{
      p: 3,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      borderLeft: `4px solid ${color}`,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box sx={{ color, mr: 1 }}>{icon}</Box>
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
    </Box>
    <Typography variant="h4" component="div" fontWeight="bold">
      {value}
    </Typography>
  </Paper>
);

const HealthCard = ({ service }: { service: ServiceHealth }) => {
  const getStatusColor = (status: ServiceHealth['status']) => {
    switch (status) {
      case 'healthy':
        return '#4caf50';
      case 'degraded':
        return '#ff9800';
      case 'down':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        borderLeft: `4px solid ${getStatusColor(service.status)}`,
      }}
    >
      <Typography variant="h6" gutterBottom>
        {service.name}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Status:
        </Typography>
        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{ color: getStatusColor(service.status), textTransform: 'uppercase' }}
        >
          {service.status}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Response Time:
        </Typography>
        <Typography variant="body2">{service.responseTime}ms</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Last checked: {new Date(service.lastCheck).toLocaleString()}
      </Typography>
    </Paper>
  );
};

const AdminDashboardPage = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [metrics, setMetrics] = useState<SystemMetrics>({
    totalUsers: { athlete: 0, trainer: 0, both: 0, superuser: 0 },
    activeUsers: 0,
    totalActivities: 0,
    dbSize: '0 GB',
    cacheHitRate: 0,
    celeryQueues: { default: 0, priority: 0, sync: 0 },
  });
  const [services, setServices] = useState<ServiceHealth[]>([]);
  const [registrations, setRegistrations] = useState<UserRegistration[]>([]);
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (isOffline) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Simulated API calls - replace with actual RTK Query endpoints
        const mockMetrics: SystemMetrics = {
          totalUsers: { athlete: 1250, trainer: 85, both: 42, superuser: 3 },
          activeUsers: 487,
          totalActivities: 45678,
          dbSize: '12.4 GB',
          cacheHitRate: 94.2,
          celeryQueues: { default: 12, priority: 3, sync: 8 },
        };

        const mockServices: ServiceHealth[] = [
          {
            name: 'API Server',
            status: 'healthy',
            responseTime: 45,
            lastCheck: new Date().toISOString(),
          },
          {
            name: 'Database Shard 1',
            status: 'healthy',
            responseTime: 12,
            lastCheck: new Date().toISOString(),
          },
          {
            name: 'Database Shard 2',
            status: 'healthy',
            responseTime: 15,
            lastCheck: new Date().toISOString(),
          },
          {
            name: 'Redis Cache',
            status: 'healthy',
            responseTime: 3,
            lastCheck: new Date().toISOString(),
          },
          {
            name: 'Celery Workers',
            status: 'degraded',
            responseTime: 120,
            lastCheck: new Date().toISOString(),
          },
        ];

        const mockRegistrations: UserRegistration[] = Array.from({ length: 30 }, (_, i) => ({
          date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          count: Math.floor(Math.random() * 20) + 5,
        }));

        const mockErrors: ErrorLog[] = [
          {
            timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
            service: 'Celery Worker',
            message: 'Task timeout: sync_strava_activities for user 1234',
            severity: 'warning',
          },
          {
            timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
            service: 'API Server',
            message: 'Rate limit exceeded for IP 192.168.1.100',
            severity: 'warning',
          },
          {
            timestamp: new Date(Date.now() - 120 * 60 * 1000).toISOString(),
            service: 'Database Shard 2',
            message: 'Connection pool exhausted',
            severity: 'error',
          },
        ];

        setMetrics(mockMetrics);
        setServices(mockServices);
        setRegistrations(mockRegistrations);
        setErrors(mockErrors);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isOffline]);

  const handleTriggerSync = async () => {
    if (isOffline) return;
    console.log('Triggering manual sync for all users...');
    // Implement actual sync trigger via API
  };

  const handleClearCache = async () => {
    if (isOffline) return;
    console.log('Clearing Redis cache...');
    // Implement actual cache clear via API
  };

  const registrationChartData = {
    labels: registrations.map((r) => r.date),
    datasets: [
      {
        label: 'New User Registrations',
        data: registrations.map((r) => r.count),
        borderColor: '#0d47a1',
        backgroundColor: 'rgba(13, 71, 161, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  };

  const totalUsers =
    metrics.totalUsers.athlete +
    metrics.totalUsers.trainer +
    metrics.totalUsers.both +
    metrics.totalUsers.superuser;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {isOffline && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You are currently offline. Dashboard functionality is limited. Some features may not be
          available.
        </Alert>
      )}

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          System Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleTriggerSync}
            disabled={isOffline || loading}
          >
            Trigger Sync
          </Button>
          <Button
            variant="outlined"
            startIcon={<ClearCacheIcon />}
            onClick={handleClearCache}
            disabled={isOffline || loading}
            color="warning"
          >
            Clear Cache
          </Button>
        </Box>
      </Box>

      {/* System Overview Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Total Users"
            value={totalUsers.toLocaleString()}
            icon={<PeopleIcon />}
            color="#0d47a1"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Active Users (24h)"
            value={metrics.activeUsers.toLocaleString()}
            icon={<PeopleIcon />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Total Activities"
            value={metrics.totalActivities.toLocaleString()}
            icon={<ActivityIcon />}
            color="#2196f3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Database Size"
            value={metrics.dbSize}
            icon={<StorageIcon />}
            color="#42a5f5"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Cache Hit Rate"
            value={`${metrics.cacheHitRate.toFixed(1)}%`}
            icon={<SpeedIcon />}
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Default Queue"
            value={metrics.celeryQueues.default}
            icon={<QueueIcon />}
            color="#ff9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Priority Queue"
            value={metrics.celeryQueues.priority}
            icon={<QueueIcon />}
            color="#f44336"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Sync Queue"
            value={metrics.celeryQueues.sync}
            icon={<MemoryIcon />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* User Breakdown */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          User Distribution by Role
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" color="text.secondary">
              Athletes
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {metrics.totalUsers.athlete.toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" color="text.secondary">
              Trainers
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {metrics.totalUsers.trainer.toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" color="text.secondary">
              Both Roles
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {metrics.totalUsers.both.toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" color="text.secondary">
              Superusers
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {metrics.totalUsers.superuser.toLocaleString()}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Service Health Status */}
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Service Health
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {services.map((service) => (
          <Grid item xs={12} sm={6} md={4} key={service.name}>
            <HealthCard service={service} />
          </Grid>
        ))}
      </Grid>

      {/* Recent User Registrations Chart */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          User Registrations (Last 30 Days)
        </Typography>
        <Box sx={{ height: 300 }}>
          <Line data={registrationChartData} options={chartOptions} />
        </Box>
      </Paper>

      {/* Recent Errors Log */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Recent Errors & Warnings
        </Typography>
        {errors.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No recent errors or warnings.
          </Typography>
        ) : (
          <Box>
            {errors.map((error, index) => (
              <Alert
                key={index}
                severity={error.severity}
                sx={{ mb: 2 }}
                icon={false}
              >
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {error.service} - {new Date(error.timestamp).toLocaleString()}
                  </Typography>
                  <Typography variant="body2">{error.message}</Typography>
                </Box>
              </Alert>
            ))}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default AdminDashboardPage;